import * as React from 'react';
import { Pencil } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HoverActionFieldProps {
  /** Content to display inside the field */
  children: React.ReactNode;
  /** Additional CSS classes on the wrapper element */
  className?: string;
  /** Renders a static, always-visible dimmed icon instead of a hover-only clickable one — takes precedence over `onClick` */
  disabled?: boolean;
  /** Icon color class — defaults to `text-primary` */
  iconColorClassName?: string;
  /** Trailing action icon — defaults to lucide `Pencil` */
  icon?: LucideIcon;
  /** When provided, the wrapper becomes an interactive element with hover affordance */
  onClick?: () => void;
  /** Title attribute for accessibility when onClick is provided */
  title?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Hover-reveal trailing action icon wrapper.
 *
 * - `disabled`: static wrapper, icon always visible and dimmed, no click handling.
 * - `onClick` (no `disabled`): `<button>` with hover background and icon fading in on hover.
 * - Neither: static `<div>`, no icon.
 */
export function HoverActionField({
  children,
  className,
  disabled = false,
  icon: Icon = Pencil,
  iconColorClassName = 'text-primary',
  onClick,
  title,
}: HoverActionFieldProps): React.JSX.Element {
  const baseClasses = cn('-mx-1 px-1 py-0.5 w-[calc(100%+0.5rem)] flex items-center gap-1.5 rounded-md', className);

  if (disabled) {
    return (
      <div className={baseClasses} title={title}>
        <span className="min-w-0">{children}</span>
        <span className={cn('flex items-center shrink-0 ms-auto opacity-40', iconColorClassName)}>
          <Icon className="w-3.5 h-3.5" />
        </span>
      </div>
    );
  }

  if (onClick !== undefined) {
    return (
      <button
        className={cn(baseClasses, 'cursor-pointer hover:bg-accent transition-colors text-left group')}
        onClick={onClick}
        title={title}
        type="button"
      >
        <span className="min-w-0">{children}</span>
        <span
          className={cn(
            'flex items-center shrink-0 ms-auto opacity-0 group-hover:opacity-100 transition-opacity',
            iconColorClassName,
          )}
        >
          <Icon className="w-3.5 h-3.5" />
        </span>
      </button>
    );
  }

  return <div className={baseClasses}>{children}</div>;
}
